import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { leaderboardsApi, statsApi } from '../lib/api';
import {
    Trophy, ArrowLeft, Medal, TrendingUp, School,
    MapPin, Users, ChevronUp, ChevronDown, Minus
} from 'lucide-react';
import MaraSportsLogo from '../components/MaraSportsLogo';

const LeaderboardsPage = () => {
    const navigate = useNavigate();
    const [sports, setSports] = useState([]);
    const [selectedSport, setSelectedSport] = useState('Football');
    const [leaderboard, setLeaderboard] = useState([]);
    const [schoolRankings, setSchoolRankings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('athletes');

    const countries = ['Kenya', 'Ghana', 'Nigeria', 'Rwanda', 'Senegal', 'Zimbabwe'];
    const ageCategories = ['U10', 'U12', 'U15', 'U18', 'U23'];

    useEffect(() => {
        loadSports();
    }, []);

    useEffect(() => {
        if (selectedSport) {
            loadLeaderboard();
        }
    }, [selectedSport]);

    const loadSports = async () => {
        try {
            const response = await statsApi.getSports();
            setSports(response.data.sports);
        } catch (error) {
            console.error('Failed to load sports:', error);
        }
    };

    const loadLeaderboard = async () => {
        setLoading(true);
        try {
            const [athleteRes, schoolRes] = await Promise.all([
                leaderboardsApi.getBySport(selectedSport, { limit: 20 }),
                leaderboardsApi.getSchoolRankings({ sport: selectedSport, limit: 20 })
            ]);
            setLeaderboard(athleteRes.data);
            setSchoolRankings(schoolRes.data);
        } catch (error) {
            console.error('Failed to load leaderboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRankChange = (idx) => {
        // Simulated rank change for demo
        const changes = [0, 1, -1, 2, 0, -2, 1, 0, -1, 3];
        return changes[idx % changes.length];
    };

    const getRankChangeIcon = (change) => {
        if (change > 0) return <ChevronUp className="w-4 h-4 text-success" />;
        if (change < 0) return <ChevronDown className="w-4 h-4 text-destructive" />;
        return <Minus className="w-4 h-4 text-muted-foreground" />;
    };

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />

            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-4 h-4" />
                            </Link>
                            <Link to="/" className="flex items-center gap-2">
                                <MaraSportsLogo className="h-8" />
                                <span className="font-heading font-bold text-lg tracking-tight uppercase hidden sm:block">
                                    NextGen<span className="text-primary"> Tournaments</span>
                                </span>
                            </Link>
                        </div>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            variant="outline"
                            className="border-white/20 text-sm"
                            data-testid="nav-dashboard"
                        >
                            Dashboard
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative py-16 md:py-24 border-b border-white/5">
                <div className="hero-glow absolute inset-0 z-0" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
                    <p className="text-xs tracking-widest uppercase text-success mb-4">Rankings & Stats</p>
                    <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter uppercase mb-4">
                        Leaderboards
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-xl">
                        ESPN-style rankings for African athletes and schools. Track performance, compare stats, and see who's rising.
                    </p>
                </div>
            </section>

            {/* Sport Selector */}
            <section className="py-6 border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex flex-wrap gap-3">
                        {sports.map((sport) => (
                            <Button
                                key={sport}
                                variant={selectedSport === sport ? 'default' : 'outline'}
                                onClick={() => setSelectedSport(sport)}
                                className={selectedSport === sport 
                                    ? 'bg-primary text-white' 
                                    : 'border-white/10 hover:border-white/20'
                                }
                                data-testid={`sport-btn-${sport.toLowerCase()}`}
                            >
                                {sport}
                            </Button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Leaderboard Content */}
            <section className="py-12">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
                        <TabsList className="bg-[#0d2847] border border-white/5 p-1 w-full md:w-auto">
                            <TabsTrigger 
                                value="athletes" 
                                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6"
                                data-testid="tab-athletes"
                            >
                                <Users className="w-4 h-4 mr-2" />
                                Athletes
                            </TabsTrigger>
                            <TabsTrigger 
                                value="schools" 
                                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6"
                                data-testid="tab-schools"
                            >
                                <School className="w-4 h-4 mr-2" />
                                Schools
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="athletes">
                            {loading ? (
                                <div className="flex items-center justify-center py-24">
                                    <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : leaderboard.length === 0 ? (
                                <div className="text-center py-24">
                                    <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                                    <h3 className="font-heading font-bold text-xl uppercase mb-2">No Rankings Yet</h3>
                                    <p className="text-muted-foreground">Rankings for {selectedSport} will appear soon.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* Header */}
                                    <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 text-xs tracking-widest uppercase text-muted-foreground">
                                        <div className="col-span-1">Rank</div>
                                        <div className="col-span-4">Athlete</div>
                                        <div className="col-span-2">Country</div>
                                        <div className="col-span-2">School</div>
                                        <div className="col-span-2">Category</div>
                                        <div className="col-span-1 text-right">Points</div>
                                    </div>

                                    {/* Rows */}
                                    {leaderboard.map((athlete, index) => {
                                        const rankChange = getRankChange(index);
                                        return (
                                            <Link
                                                key={athlete.athlete_id || index}
                                                to={`/athletes/${athlete.athlete_id}`}
                                                className={`block bg-[#0d2847] border border-white/5 hover:border-white/10 transition-colors ${index < 3 ? 'border-l-2' : ''} ${index === 0 ? 'border-l-accent' : index === 1 ? 'border-l-gray-400' : index === 2 ? 'border-l-amber-700' : ''}`}
                                                data-testid={`leaderboard-row-${index}`}
                                            >
                                                <div className="grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 items-center">
                                                    {/* Rank */}
                                                    <div className="col-span-1 flex items-center gap-2">
                                                        <span className={`stat-display text-2xl ${index === 0 ? 'text-accent' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                                                            {athlete.rank}
                                                        </span>
                                                        {getRankChangeIcon(rankChange)}
                                                    </div>
                                                    
                                                    {/* Name */}
                                                    <div className="col-span-1 md:col-span-4">
                                                        <p className="font-medium hover:text-primary transition-colors">{athlete.name}</p>
                                                    </div>
                                                    
                                                    {/* Country */}
                                                    <div className="hidden md:flex md:col-span-2 items-center gap-2 text-sm text-muted-foreground">
                                                        <MapPin className="w-3 h-3" />
                                                        {athlete.country}
                                                    </div>
                                                    
                                                    {/* School */}
                                                    <div className="hidden md:block md:col-span-2 text-sm text-muted-foreground truncate">
                                                        {athlete.school || '-'}
                                                    </div>
                                                    
                                                    {/* Category */}
                                                    <div className="hidden md:block md:col-span-2">
                                                        {athlete.age_category && (
                                                            <span className="px-2 py-1 bg-white/5 text-xs">{athlete.age_category}</span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Points */}
                                                    <div className="col-span-1 text-right">
                                                        <span className="stat-display text-xl text-primary">{athlete.points}</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="schools">
                            {loading ? (
                                <div className="flex items-center justify-center py-24">
                                    <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : schoolRankings.length === 0 ? (
                                <div className="text-center py-24">
                                    <School className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                                    <h3 className="font-heading font-bold text-xl uppercase mb-2">No School Rankings Yet</h3>
                                    <p className="text-muted-foreground">School rankings for {selectedSport} will appear soon.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* Header */}
                                    <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 text-xs tracking-widest uppercase text-muted-foreground">
                                        <div className="col-span-1">Rank</div>
                                        <div className="col-span-3">School</div>
                                        <div className="col-span-2">Location</div>
                                        <div className="col-span-1">Wins</div>
                                        <div className="col-span-1">Losses</div>
                                        <div className="col-span-2">Tournaments</div>
                                        <div className="col-span-2 text-right">Points</div>
                                    </div>

                                    {/* Rows */}
                                    {schoolRankings.map((school, index) => (
                                        <div
                                            key={school.id || index}
                                            className={`bg-[#0d2847] border border-white/5 ${index < 3 ? 'border-l-2' : ''} ${index === 0 ? 'border-l-accent' : index === 1 ? 'border-l-gray-400' : index === 2 ? 'border-l-amber-700' : ''}`}
                                            data-testid={`school-row-${index}`}
                                        >
                                            <div className="grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 items-center">
                                                {/* Rank */}
                                                <div className="col-span-1">
                                                    <span className={`stat-display text-2xl ${index === 0 ? 'text-accent' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                                                        {school.rank}
                                                    </span>
                                                </div>
                                                
                                                {/* Name */}
                                                <div className="col-span-1 md:col-span-3">
                                                    <p className="font-medium">{school.school_name}</p>
                                                    <p className="text-xs text-muted-foreground md:hidden">{school.city}, {school.country}</p>
                                                </div>
                                                
                                                {/* Location */}
                                                <div className="hidden md:flex md:col-span-2 items-center gap-2 text-sm text-muted-foreground">
                                                    <MapPin className="w-3 h-3" />
                                                    {school.city}, {school.country}
                                                </div>
                                                
                                                {/* Wins */}
                                                <div className="hidden md:block md:col-span-1 text-sm text-success">
                                                    {school.wins}W
                                                </div>
                                                
                                                {/* Losses */}
                                                <div className="hidden md:block md:col-span-1 text-sm text-destructive">
                                                    {school.losses}L
                                                </div>
                                                
                                                {/* Tournaments */}
                                                <div className="hidden md:block md:col-span-2 text-sm text-muted-foreground">
                                                    {school.tournaments_participated} events
                                                </div>
                                                
                                                {/* Points */}
                                                <div className="col-span-1 md:col-span-2 text-right">
                                                    <span className="stat-display text-xl text-primary">{school.points}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </section>
        </div>
    );
};

export default LeaderboardsPage;
